import {
  createContext,
  useContext,
  useState,
  useEffect,
} from 'react';
import type { ReactNode } from 'react';
import type { AuthState } from '../types';
import api from '../api/client';

interface AuthContextType extends AuthState {
  login: (returnUrl?: string) => void;
  logout: () => Promise<void>;
  setToken: (token: string) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'vibe_token';
const RETURN_URL_KEY = 'vibe_auth_return_url';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Load token from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem(TOKEN_KEY);
    if (savedToken) {
      api.setToken(savedToken);
      fetchUser(savedToken);
    } else {
      setState((s) => ({ ...s, isLoading: false }));
    }
  }, []);

  const fetchUser = async (token: string) => {
    try {
      const response = await api.getMe();
      setState({
        user: response.user,
        token,
        isLoading: false,
        isAuthenticated: true,
      });
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem(TOKEN_KEY);
      api.setToken(null);
      setState({
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  };

  const login = (returnUrl?: string) => {
    // Save return URL to localStorage so we can redirect back after OAuth
    if (returnUrl) {
      localStorage.setItem(RETURN_URL_KEY, returnUrl);
    } else {
      localStorage.removeItem(RETURN_URL_KEY);
    }
    // Redirect to GitHub OAuth
    window.location.href = api.getGitHubAuthUrl();
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem(TOKEN_KEY);
    api.setToken(null);
    setState({
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,
    });
  };

  const setToken = (token: string) => {
    localStorage.setItem(TOKEN_KEY, token);
    api.setToken(token);
    fetchUser(token);
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        setToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
